import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../lib/cn.js'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'children'> {
  label?: string
  error?: string
  hint?: string
  options: SelectOption[]
  placeholder?: string
  startIcon?: ReactNode
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, label, error, hint, options, placeholder, startIcon, id, ...props },
  ref,
) {
  const autoId = useId()
  const selectId = id ?? autoId
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
          {props.required && <span className="text-danger ms-1" aria-hidden>*</span>}
        </label>
      )}

      <div className="relative">
        {startIcon && (
          <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-fg-subtle">
            {startIcon}
          </span>
        )}

        <select
          ref={ref}
          id={selectId}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-10 w-full appearance-none rounded-lg border bg-surface px-3 pe-9 text-sm text-fg',
            'transition-[border-color,box-shadow] duration-150',
            'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
            'disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-bg-subtle',
            startIcon && 'ps-10',
            error ? 'border-danger focus:border-danger focus:ring-danger/20' : 'border-border',
            className,
          )}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>

        <ChevronDown className="pointer-events-none absolute inset-y-0 end-3 my-auto size-4 text-fg-subtle" aria-hidden />
      </div>

      {error ? (
        <p id={`${selectId}-error`} role="alert" className="mt-1.5 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p id={`${selectId}-hint`} className="mt-1.5 text-xs text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  )
})
